import { EOrderType } from '@aqx-core/common/api/order';

import { IPostEventAskPriceVisibleMessage } from './askPriceVisible';
import { IPostEventUpdateCandlestickColorMessage } from './candlestick';
import {
  IPostEventChartDataMessage,
  IPostEventChartFeedMessage,
  IPostEventChartLoadMessage,
  IPostEventChartPatchMessage,
  IPostEventSetSymbolMessage,
  IPostEventSymbolFeedMessage,
  IReceiveEventChartFetchMoreMessage
} from './chart';
import { IReceiveEventOpenContextMenuMessage } from './contextMenu';
import {
  IPostEventOpenDrawingMessage,
  IPostEventRemoveDrawingMessage,
  IReceiveEventCompleteDrawingMessage,
  IReceiveEventDeleteDrawingMessage,
  IReceiveEventSelectDrawingToolMessage
} from './draw';
import {
  IPostEventIndicatorMessage,
  IPostEventUpdateIndicatorFormMessage,
  IReceiveEventIndicatorMessage,
  IReceiveEventOpenIndicatorFormMessage
} from './indicator';
import { IPostEventIsNativeMessage } from './is-native';
import { IPostEventSetLanguageMessage } from './language';
import { IPostEventMiniChartDataMessage, IReceiveEventMiniChartStatusMessage } from './mini-chart';
import { IPostEventPeriodMessage } from './period';
import {
  IPostEventBatchCancelPriceAlertMessage,
  IPostEventRemovePriceAlertsMessage,
  IPostEventUpdatePriceAlertsMessage,
  IReceiveEventOpenPriceAlertConfigMessage
} from './priceAlert';
import { IPostEventResetMessage, IPostEventResizeMessage, IPostEventScrollToLatestMessage } from './reset';
import { IPostEventSeriesMessage } from './series';
import { IReceiveEventEnqueueSnackbarMessage } from './snackbar';
import { IReceiveEventStatusMessage } from './status';
import { IPostEventThemeSchemeMessage } from './theme';
import { IPostEventTokenMessage } from './token';
import {
  IPostEventBatchCancelTrackOrderMessage,
  IPostEventCancelTrackOrderMessage,
  IPostEventRevertEditTrackOrderPriceMessage,
  IPostEventTrackOrderMessage,
  IPostEventUpdateTrackOrderPriceMessage,
  IReceiveEventCloseOrderMessage,
  IReceiveEventEditTrackOrderPriceMessage
} from './track';
import { IPostEventTVSetThemeMessage } from './tv-change-theme';
import { IPostEventTVSetInitialConfigsMessage } from './tv-set-initial-configs';
import { IPostEventTVSetLanguageMessage } from './tv-set-language';
import { IPostEventTVSetPeriodMessage } from './tv-set-period';
import { IPostEventTVToggleIndicatorsMessage } from './tv-toggle-indicators';

export * from './indicator';
export * from './status';
export * from './event';
export * from './provider';
export * from './priceAlert';

/** Max digits for indicator inputs. Keeps values within JS safe integer range. */
export const INDICATOR_INPUT_MAX_DIGITS = 15;

export enum EChartType {
  ASSETS = 'ASSETS',
  MARKET = 'MARKET',
  PROFIT_LOSS = 'PROFIT_LOSS',
  MINI_CHART = 'MINI',
  RETURN_HISTORY = 'RETURN_HISTORY'
}

export enum EChartSource {
  WEB = 'WEB',
  MOBILEWEB = 'MOBILEWEB',
  MOBILEAPP = 'MOBILEAPP'
}

export interface IOrderTracker {
  symbol: string;
  orderId: string;
  decimal: number;
  orderType: EOrderType;
  stopLoss: number;
  takeProfit: number;
  entryPrice: number;
  isEditable: boolean;
  isClosable: boolean;
  additionalPrice?: number;
}

export interface IGetEntryAndAdditionalPricesParams {
  orderType: EOrderType | undefined;
  price: number;
  priceTrigger: number;
}

export interface IGetEntryPriceInfoParams {
  orderType: EOrderType;
  decimal: number;
  entryPrice: number;
  additionalPrice?: number;
}

export type IPostEventMessage =
  | IPostEventTokenMessage
  | IPostEventIndicatorMessage
  | IPostEventPeriodMessage
  | IPostEventSeriesMessage
  | IPostEventScrollToLatestMessage
  | IPostEventAskPriceVisibleMessage
  | IPostEventUpdatePriceAlertsMessage
  | IPostEventRemovePriceAlertsMessage
  | IPostEventBatchCancelPriceAlertMessage
  | IPostEventResetMessage
  | IPostEventRemoveDrawingMessage
  | IPostEventBatchCancelTrackOrderMessage
  | IPostEventOpenDrawingMessage
  | IPostEventResizeMessage
  | IPostEventChartDataMessage
  | IPostEventSymbolFeedMessage
  | IPostEventChartFeedMessage
  | IPostEventChartPatchMessage
  | IPostEventChartLoadMessage
  | IPostEventUpdateIndicatorFormMessage
  | IPostEventSetSymbolMessage
  | IPostEventThemeSchemeMessage
  | IPostEventIsNativeMessage
  | IPostEventUpdateTrackOrderPriceMessage
  | IPostEventTrackOrderMessage
  | IPostEventCancelTrackOrderMessage
  | IPostEventRevertEditTrackOrderPriceMessage
  | IPostEventUpdateTrackOrderPriceMessage
  | IPostEventTVSetPeriodMessage
  | IPostEventTVSetThemeMessage
  | IPostEventTVSetInitialConfigsMessage
  | IPostEventTVToggleIndicatorsMessage
  | IPostEventTVSetLanguageMessage
  | IPostEventUpdateCandlestickColorMessage
  | IPostEventSetLanguageMessage
  | IPostEventMiniChartDataMessage;

export type IReceiveEventMessage =
  | IReceiveEventIndicatorMessage
  | IReceiveEventStatusMessage
  | IReceiveEventOpenContextMenuMessage
  | IReceiveEventOpenIndicatorFormMessage
  | IReceiveEventChartFetchMoreMessage
  | IReceiveEventSelectDrawingToolMessage
  | IReceiveEventCompleteDrawingMessage
  | IReceiveEventDeleteDrawingMessage
  | IReceiveEventCloseOrderMessage
  | IReceiveEventEditTrackOrderPriceMessage
  | IReceiveEventEnqueueSnackbarMessage
  | IReceiveEventMiniChartStatusMessage
  | IReceiveEventOpenPriceAlertConfigMessage;

export interface IInitPriceAlertParams {
  symbol?: string;
  cleanBeforeDispatch?: boolean;
}
